import { MaturityAssessmentFilter } from '@constants/entities/project-dashboard';
import { SortDirection } from '@store/services/types';
import { ProjectDashboardFiltersStore } from '@store/zustand/project-dashboard-filters/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const initialMaturityView = (): Pick<
  ProjectDashboardFiltersStore,
  'assessmentFilter' | 'maturitySorting'
> => ({
  assessmentFilter: MaturityAssessmentFilter.All,
  maturitySorting: { gap: SortDirection.DESC },
});

// A selection made while scopeKey is null belongs to no bucket and is dropped by the first setScope.
const useProjectDashboardFiltersStore = create<ProjectDashboardFiltersStore>(
  (set, get) => ({
    partners: [],
    scopeKey: null,
    buckets: {},
    ...initialMaturityView(),

    setPartners: (partners) => set({ partners }),

    removePartner: (partner) =>
      set({ partners: get().partners.filter((item) => item !== partner) }),

    clearPartners: () => set({ partners: [] }),

    setScope: (key) => {
      const { scopeKey, buckets, partners } = get();

      if (scopeKey === key) {
        return;
      }

      const nextBuckets = scopeKey
        ? { ...buckets, [scopeKey]: partners }
        : buckets;

      set({
        partners: key ? nextBuckets[key] ?? [] : [],
        scopeKey: key,
        buckets: nextBuckets,
      });
    },

    reset: () =>
      set({
        partners: [],
        scopeKey: null,
        buckets: {},
        ...initialMaturityView(),
      }),

    setAssessmentFilter: (assessmentFilter) => set({ assessmentFilter }),

    setMaturitySorting: (maturitySorting) => set({ maturitySorting }),

    resetMaturityView: () => set(initialMaturityView()),

    clearDashboardFilters: () =>
      set({ partners: [], ...initialMaturityView() }),
  }),
);

export const useProjectDashboardFilters = () =>
  useProjectDashboardFiltersStore(useShallow(({ partners }) => ({ partners })));

export const useMaturityViewState = () =>
  useProjectDashboardFiltersStore(
    useShallow(({ assessmentFilter, maturitySorting }) => ({
      assessmentFilter,
      maturitySorting,
    })),
  );

export const useProjectDashboardActions = () =>
  useProjectDashboardFiltersStore(
    useShallow(
      ({
        setPartners,
        removePartner,
        clearPartners,
        setScope,
        reset,
        setAssessmentFilter,
        setMaturitySorting,
        resetMaturityView,
        clearDashboardFilters,
      }) => ({
        setPartners,
        removePartner,
        clearPartners,
        setScope,
        reset,
        setAssessmentFilter,
        setMaturitySorting,
        resetMaturityView,
        clearDashboardFilters,
      }),
    ),
  );

export default useProjectDashboardFiltersStore;
